
import Layout from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { ShoppingBag } from "lucide-react";
import { Link } from "react-router-dom";
import ProductCard from "@/components/product/ProductCard";
import BlogCard from "@/components/blog/BlogCard";
import Newsletter from "@/components/newsletter/Newsletter";
import { products } from "@/data/products";
import { blogPosts } from "@/data/blog-posts";
import ScrollReveal, { StaggerContainer, StaggerItem } from "@/components/ui/scroll-reveal";
import { motion } from "framer-motion";

const Index = () => {
  const featuredProducts = products.filter(product => product.featured);

  return (
    <Layout>
      {/* Hero Section with animated gradient */}
      <section className="pt-40 pb-32 md:pt-48 md:pb-40 px-4 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-bunnie-cream via-bunnie-peach/20 to-bunnie-background animate-gradient-shift" />
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1518495973542-4542c06a5843?q=30&w=800&auto=format&fit=crop')] opacity-5 bg-cover bg-center" />
        <div className="container-custom relative">
          <div className="mx-auto text-center max-w-2xl">
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="font-serif text-4xl md:text-6xl uppercase font-semibold text-bunnie-brown mb-12 leading-tight tracking-wide">
                Glow Naturally. Care for Every Curl, Coil, and Skin Type.
              </h1>
            </motion.div>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="text-lg md:text-xl text-bunnie-brown/70 mb-16 font-medium"
            >
              Minimal ingredients. Maximum nourishment.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <Link to="/shop" className="block mx-auto w-fit">
                <motion.div whileHover={{ y: -3 }} whileTap={{ scale: 0.97 }}>
                  <Button className="bg-bunnie-brown text-white hover:bg-opacity-95 transition-all px-16 py-7 rounded-full font-medium shadow-md hover:shadow-lg hover:border hover:border-bunnie-peach text-center mx-auto" size="lg">
                    <ShoppingBag className="h-5 w-5 mr-2" /> Shop the Collection
                  </Button>
                </motion.div>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Brand Mission */}
      <section className="section-padding bg-bunnie-peach/10">
        <div className="container-custom">
          <ScrollReveal>
            <div className="text-center mb-10">
              <h2 className="font-serif text-3xl md:text-4xl text-bunnie-brown mb-4">Our Story</h2>
            </div>
          </ScrollReveal>
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <ScrollReveal variant="fadeLeft" className="order-2 md:order-1 flex flex-col justify-center items-center md:items-start">
              <h3 className="font-serif text-2xl md:text-3xl text-bunnie-brown mb-6 text-center md:text-left">Rooted in Nature, Crafted with Care</h3>
              <p className="text-bunnie-brown/80 text-lg mb-6 text-center md:text-left">
                Bunnie Essience was born from a desire to keep beauty simple. Our formulas are made with minimal ingredients, crafted for every hair and skin type with care, clarity, and compassion.
              </p>
              <div className="flex justify-center w-full md:justify-start">
                <Link to="/about">
                  <Button variant="outline" className="border-bunnie-brown text-bunnie-brown hover:bg-bunnie-brown hover:text-white">
                    Learn More About Our Story
                  </Button>
                </Link>
              </div>
            </ScrollReveal>
            <ScrollReveal variant="fadeRight" className="order-1 md:order-2 grid grid-cols-2 gap-4">
              <img src="/lovable-uploads/e2a6b01c-2678-46b4-a509-337693ae5e96.webp" alt="Bunnie Essience Hair Care Products" className="rounded-lg shadow-lg w-full object-cover" loading="lazy" width="600" height="600" />
              <img src="/lovable-uploads/a4219196-ff6b-44e4-9294-1a5335be3f9d.webp" alt="Bunnie Essience Skin Care Products" className="rounded-lg shadow-lg w-full object-cover" loading="lazy" width="600" height="600" />
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="section-padding">
        <div className="container-custom">
          <ScrollReveal>
            <div className="text-center mb-12">
              <h2 className="font-serif text-3xl md:text-4xl text-bunnie-brown mb-4 uppercase tracking-wider">OUR ESSENTIALS</h2>
              <p className="text-bunnie-brown/70 max-w-2xl mx-auto">
                Discover our most loved formulas, crafted with care and clean ingredients.
              </p>
            </div>
          </ScrollReveal>

          <StaggerContainer className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {featuredProducts.map((product) => (
              <StaggerItem key={product.id}>
                <ProductCard
                  id={product.id}
                  name={product.name}
                  price={product.price}
                  image={product.image}
                  category={product.category}
                  description={product.description}
                  ingredients={product.ingredients}
                  rating={product.rating}
                  reviews={product.reviews}
                  type={product.type}
                />
              </StaggerItem>
            ))}
          </StaggerContainer>

          <ScrollReveal delay={0.3}>
            <div className="text-center mt-12">
              <Link to="/shop">
                <Button variant="outline" className="border-bunnie-brown text-bunnie-brown hover:bg-bunnie-brown hover:text-white">
                  View All Products
                </Button>
              </Link>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Ingredient Transparency */}
      <section className="section-padding bg-bunnie-peach/10">
        <div className="container-custom">
          <ScrollReveal variant="scaleUp">
            <div className="text-center max-w-2xl mx-auto">
              <h2 className="font-serif text-3xl md:text-4xl text-bunnie-brown mb-4">Know What You're Using</h2>
              <p className="text-bunnie-brown/70 mb-8">
                We believe in full transparency. Every ingredient is carefully selected and fully documented in our glossary.
              </p>
              <Link to="/ingredients">
                <Button variant="outline" className="border-bunnie-brown text-bunnie-brown hover:bg-bunnie-brown hover:text-white">
                  View Ingredient Glossary
                </Button>
              </Link>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Blog Preview */}
      <section className="section-padding">
        <div className="container-custom">
          <ScrollReveal>
            <div className="text-center mb-12">
              <h2 className="font-serif text-3xl md:text-4xl text-bunnie-brown mb-4">The Bunnie Journal</h2>
              <p className="text-bunnie-brown/70 max-w-2xl mx-auto">
                Tips, tricks, and insights for your natural beauty journey.
              </p>
            </div>
          </ScrollReveal>

          <StaggerContainer className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {blogPosts.map((post) => (
              <StaggerItem key={post.id}>
                <BlogCard id={post.id} title={post.title} excerpt={post.excerpt} image={post.image} date={post.date} category={post.category} />
              </StaggerItem>
            ))}
          </StaggerContainer>

          <ScrollReveal delay={0.3}>
            <div className="text-center mt-12">
              <Link to="/blog">
                <Button variant="outline" className="border-bunnie-brown text-bunnie-brown hover:bg-bunnie-brown hover:text-white">
                  Read More Articles
                </Button>
              </Link>
            </div>
          </ScrollReveal>
        </div>
      </section>

      <ScrollReveal variant="blur">
        <Newsletter />
      </ScrollReveal>
    </Layout>
  );
};

export default Index;
